import React, { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const clampIndex = (index, count) => {
  if (count <= 0) return 0;
  return Math.max(0, Math.min(index, count - 1));
};

const FacilityCarousel = ({ images = [], initialIndex = 0 }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [animPhase, setAnimPhase] = useState(1);
  const [direction, setDirection] = useState('next');

  useEffect(() => {
    setActiveIndex(clampIndex(initialIndex, images.length));
  }, [initialIndex, images.length]);

  useEffect(() => {
    setActiveIndex((prev) => clampIndex(prev, images.length));
  }, [images.length]);

  useEffect(() => {
    if (images.length <= 1) return undefined;
    const id = setInterval(() => {
      setDirection('next');
      setActiveIndex((prev) => ((prev + 1) % images.length) || 0);
    }, 3000);
    return () => clearInterval(id);
  }, [images.length]);

  useLayoutEffect(() => {
    setAnimPhase(0);
    const id = requestAnimationFrame(() => setAnimPhase(1));
    return () => cancelAnimationFrame(id);
  }, [activeIndex, images.length, direction]);

  const activeImage = useMemo(() => images[activeIndex] || null, [images, activeIndex]);

  const visibleImages = useMemo(() => {
    if (!images.length) return [];
    return [{ ...images[activeIndex], key: `${images[activeIndex].src}-${activeIndex}` }];
  }, [images, activeIndex]);

  const goTo = (index) => {
    const nextIndex = clampIndex(index, images.length);
    setDirection(nextIndex >= activeIndex ? 'next' : 'prev');
    setActiveIndex(nextIndex);
  };
  const next = () => {
    if (!images.length) return;
    setDirection('next');
    setActiveIndex((prev) => (prev + 1) % images.length);
  };
  const prev = () => {
    if (!images.length) return;
    setDirection('prev');
    setActiveIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const thumbnailWindow = useMemo(() => {
    const maxVisible = 6;
    const total = images.length;
    
    if (total <= maxVisible) return images.map((img, i) => ({ ...img, originalIndex: i }));
  
    let start = activeIndex - Math.floor(maxVisible / 2);
    start = Math.max(0, Math.min(start, total - maxVisible));
    
    return images
      .slice(start, start + maxVisible)
      .map((img, i) => ({ ...img, originalIndex: start + i }));
  }, [images, activeIndex]);

  // Faculty image renderer with fallback
  const FacultyImageRenderer = ({ image, className, sizes }) => {
    const FALLBACK = '/assets/2.png';
    const [src, setSrc] = useState(image?.hdSrc || image?.src || FALLBACK);

    useEffect(() => {
      setSrc(image?.hdSrc || image?.src || FALLBACK);
    }, [image]);

    const srcSet = image?.srcSet || (image?.hdSrc ? `${image.hdSrc} 2x` : undefined);

    return (
      <img
        src={src}
        srcSet={srcSet}
        sizes={sizes}
        alt={image?.alt || 'Image'}
        className={className}
        onError={() => {
          if (src !== FALLBACK) setSrc(FALLBACK);
        }}
      />
    );
  };

  return (
    <div className="w-full">
      {/* Header with Icon - Gallery Badge */}
      <div className="flex items-center justify-center mb-8">
        <div className="relative bg-[#1f6fa2] rounded-full px-8 py-3 flex items-center gap-3 shadow-lg">
          {/* decorative green base strip behind the button */}
          <span className="absolute h-12 bg-[#c7e44a] rounded-full" style={{ bottom: '-6px', left: '0', right: '0', zIndex: -1 }} />
          
          <img 
            src="/newassets/icon 3.png" 
            alt="Gallery Icon" 
            className="w-8 h-8 object-contain relative z-10"
          />
          
          <h2 className="relative z-10 text-white text-2xl font-bold tracking-wide">FACILITY</h2>
        </div>
      </div>

      <div className="relative overflow-hidden rounded-2xl">
        <div
          className="flex justify-center p-0 md:p-3"
          style={{
            opacity: animPhase,
            transform: animPhase ? 'translateX(0) scale(1)' : direction === 'next' ? 'translateX(15px) scale(0.98)' : 'translateX(-15px) scale(0.98)',
            transition: 'opacity 400ms ease, transform 400ms ease',
          }}
        >
          {visibleImages.map((image, idx) => (
            <div
              key={image.key}
              className="relative aspect-[16/9] w-[1300px] overflow-hidden rounded-xl"
              style={{
                opacity: animPhase ? 1 : 0,
                transform: animPhase 
                  ? 'translateX(0) scale(1) rotate(0deg)' 
                  : direction === 'next' 
                    ? 'translateX(30px) scale(0.95) rotate(1deg)' 
                    : 'translateX(-30px) scale(0.95) rotate(-1deg)',
                filter: 'blur(0px)',
                transition: 'opacity 550ms cubic-bezier(.2,.8,.2,1), transform 550ms cubic-bezier(.2,.8,.2,1), filter 550ms ease',
              }}
            >
              {image.date && (
                <div className="absolute left-1/2 -translate-x-1/2 top-3 z-20 px-3 py-1 rounded-full bg-[#f08c1a] text-white text-xs font-semibold">
                  {image.date}
                </div>
              )}
              <FacultyImageRenderer
                image={image}
                sizes="(max-width: 968px) 100vw, 1200px"
                className="h-full w-full object-contain"
              />
            </div>
          ))}
        </div>

        {/* Left Arrow Button - Lime Green */}
        <button
          type="button"
          aria-label="Previous image"
          className="absolute z-50 top-1/2 -translate-y-1/2 rounded-full bg-[#c7e44a] text-slate-900 shadow-lg transition-all duration-300 ease-out hover:scale-105 hover:bg-[#b8d943] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#c7e44a]"
          style={{ left: 130, padding: 12 }}
          onClick={prev}
        >
          <ChevronLeft className="w-6 h-6 md:w-7 md:h-7 stroke-[3]" />
        </button>

        {/* Right Arrow Button - Lime Green */}
        <button
          type="button"
          aria-label="Next image"
          className="absolute z-50 top-1/2 -translate-y-1/2 rounded-full bg-[#c7e44a] text-slate-900 shadow-lg transition-all duration-300 ease-out hover:scale-105 hover:bg-[#b8d943] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#c7e44a]"
          style={{ right: 130, padding: 12 }}
          onClick={next}
        >
          <ChevronRight className="w-6 h-6 md:w-7 md:h-7 stroke-[3]" />
        </button>
      </div>

      <div className="mt-2 md:mt-6 flex flex-col items-center gap-4">
        <div className="flex items-center justify-center gap-3 p-2 rounded-2xl backdrop-blur-sm">
          {thumbnailWindow.map((image) => (
            <button
              key={`thumb-${image.originalIndex}`}
              type="button"
              className={`relative h-14 w-20 sm:h-16 sm:w-24 overflow-hidden rounded-xl transition-all duration-300 ease-out 
                ${
                  image.originalIndex === activeIndex
                    ? 'border-4 border-white scale-105 shadow-lg z-10'
                    : 'border-2 border-white opacity-70 hover:opacity-100 hover:scale-105'
                }`}
              onClick={() => goTo(image.originalIndex)}
              style={{ background: '#fff' }}
            >
              <FacultyImageRenderer
                image={image}
                className="h-full w-full object-cover"
              />
              {image.originalIndex === activeIndex && (
                <div className="absolute inset-0 bg-sky-500/10 flex items-center justify-center">
                  <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />
                </div>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Pagination Dots */}
      <div className="mt-4 flex items-center justify-center gap-3">
        {images.map((_, index) => (
          <label key={`dot-${index}`} className="cursor-pointer">
            <input
              type="radio"
              className="sr-only"
              value={index}
              checked={activeIndex === index}
              aria-label={`Select slide ${index + 1}`}
              onChange={() => goTo(index)}
            />
            <span
              className={
                index === activeIndex
                  ? 'inline-block h-3 w-3 rounded-full bg-[#1f6fa2] shadow-md transition-transform transform scale-110'
                  : 'inline-block h-2 w-2 rounded-full bg-gray-400 transition-all hover:bg-gray-500'
              }
            />
          </label>
        ))}
      </div>
    </div>
  );
};

export default FacilityCarousel;